import type { PlotData } from "@/types/plot";

// ── Types ──

/**
 * How strongly an exclusion suppresses the score.
 *  - not_scored   → No composite, no sub-scores. Dignified "this isn't a market asset" card.
 *  - context_only → Sub-scores (Location, Building) may still be useful as context, but
 *                   no composite is shown because there's no market to compete in.
 *  - caveat       → Composite is shown, but a prominent badge explains the real-world
 *                   constraint (e.g. heritage restrictions heavily limit alterations).
 */
export type ExclusionTier = "not_scored" | "context_only" | "caveat";

export type ExclusionReason =
  | "memorial"         // Anne Frank House, war cemeteries, Holocaust memorials, grafmonumenten
  | "worship"          // active churches, mosques, synagogues, temples
  | "government"       // ministries, town halls, palaces, parliament
  | "diplomatic"       // embassies, consulates
  | "military"         // bases, defence sites
  | "detention"        // prisons (BAG celfunctie)
  | "park"             // public parks (bestemming: groen)
  | "water"            // surface water (bestemming: water)
  | "infrastructure"   // substations, treatment plants, transit (bestemming: verkeer)
  | "school"           // BAG onderwijsfunctie
  | "healthcare"       // BAG gezondheidszorgfunctie (hospitals, clinics)
  | "heritage_private" // Rijksmonument in private hands — shown with caveat, not suppressed
  | "beschermd_stadsgezicht_private"; // protected cityscape residence — caveat

export interface ScoreExclusion {
  tier: ExclusionTier;
  reason: ExclusionReason;
  /** Short public-facing label shown on the score badge. Localised. */
  label: { en: string; nl: string };
  /** One-sentence explanation shown in the score popover / info panel. */
  rationale: { en: string; nl: string };
  /** Which signal triggered this — useful for debugging and user-flag escalation. */
  source:
    | "denylist"
    | "heritage_api"
    | "zoning"
    | "bag_function";
  /** When `source === "denylist"`, the matched denylist entry's id. */
  denylistId?: string;
}

// ── Curated denylist ──
// Nationally-known sites where a PlotScore number would be editorially wrong.
// Keep this small and defensible: only add sites where the PR cost of scoring
// clearly outweighs any user benefit. The denylist is NOT a substitute for
// rule-based detection — it's a belt-and-braces for the ~100 cases a journalist
// would actually check.
//
// NOTE: Monument numbers below are placeholders pending verification against the
// RCE Rijksmonumentenregister. For sites without a Rijksmonumentnummer (e.g.
// Efteling, Keukenhof), use postcode + huisnummer. At least one match key per
// entry must be non-null — detectExclusion() short-circuits if every key misses.

interface DenylistEntry {
  id: string;
  tier: ExclusionTier;
  reason: ExclusionReason;
  label: { en: string; nl: string };
  rationale: { en: string; nl: string };
  /** Match signals. Entry matches if ANY non-null signal matches the plot. */
  match: {
    monumentNumbers?: number[];
    /** Exact postcode + huisnummer pairs (no spaces, uppercase postcode). */
    addresses?: { postcode: string; huisnummer: string }[];
    /**
     * Street + city + huisnummer match — postcode-free. Useful when a
     * landmark complex has multiple entrances across postcodes, or when
     * BAG geocoding resolves to a side entrance with a different postcode
     * than the canonical historic address.
     */
    streetAddresses?: { straat: string; woonplaats: string; huisnummers: string[] }[];
    /**
     * BAG pand ids. Either bare 16-digit form (`0363100012172926`) or the
     * fully-qualified NEN 3610 urn (`NL.IMBAG.Pand.0363100012172926`) —
     * normalised on both sides before comparison.
     */
    bagIds?: string[];
    /**
     * Substrings (case-insensitive, diacritic-insensitive) that must appear
     * in `plot.heritage.monumentName`. Use for landmarks where the
     * Rijksmonument has a canonical name but we don't want to pin a
     * specific monument number.
     */
    monumentNameContains?: string[];
  };
}

export const DENYLIST: DenylistEntry[] = [
  {
    id: "anne-frank-house",
    tier: "not_scored",
    reason: "memorial",
    label: {
      en: "Memorial — not scored",
      nl: "Gedenkplaats — niet beoordeeld",
    },
    rationale: {
      en: "The Anne Frank House is a memorial site. PlotScore does not apply commercial ratings to memorial or remembrance sites.",
      nl: "Het Anne Frank Huis is een gedenkplaats. PlotScore geeft geen commerciële beoordeling aan gedenk- en herinneringsplaatsen.",
    },
    match: {
      // Strongest signal: BAG pand id for the Anne Frank House complex.
      // Confirmed from the app sidebar (2026-04-15). Add additional pand ids
      // here as we learn them — the historic Achterhuis wing, the 1999
      // visitor entrance, and the Anne Frank Stichting offices are likely
      // separate panden and each has its own 16-digit id.
      bagIds: ["NL.IMBAG.Pand.0363100012172926"],
      // Rijksmonument 5785 per RCE (verify before shipping).
      monumentNumbers: [5785],
      // Name-based fallback via RCE: monumentnaam should contain "Anne Frank"
      // for the historic pand regardless of whether the number is right.
      monumentNameContains: ["anne frank"],
      addresses: [
        { postcode: "1016GV", huisnummer: "263" },
        { postcode: "1016GV", huisnummer: "265" },
        { postcode: "1016GV", huisnummer: "267" },
      ],
      // BAG often resolves the complex to the modern visitor-entrance wing on
      // Westermarkt (visitor wing built 1999, offices across adjacent numbers).
      // We include the whole small cluster around Westerkerk where the
      // foundation operates — false-positive risk is low because these
      // specific numbers on Westermarkt Amsterdam map to a tiny set of
      // buildings.
      streetAddresses: [
        { straat: "Westermarkt", woonplaats: "Amsterdam", huisnummers: ["10", "18", "20"] },
      ],
    },
  },
  {
    id: "rijksmuseum",
    tier: "context_only",
    reason: "government",
    label: { en: "National museum", nl: "Nationaal museum" },
    rationale: {
      en: "A national museum is a public cultural asset — it does not trade on the property market. Sub-scores are shown for context only.",
      nl: "Een nationaal museum is een publiek cultureel bezit — er is geen vastgoedmarkt voor. Subscores zijn uitsluitend ter context.",
    },
    match: {
      addresses: [{ postcode: "1071XX", huisnummer: "1" }],
    },
  },
  {
    id: "van-gogh-museum",
    tier: "context_only",
    reason: "government",
    label: { en: "National museum", nl: "Nationaal museum" },
    rationale: {
      en: "A national museum is a public cultural asset — it does not trade on the property market. Sub-scores are shown for context only.",
      nl: "Een nationaal museum is een publiek cultureel bezit — er is geen vastgoedmarkt voor. Subscores zijn uitsluitend ter context.",
    },
    match: {
      addresses: [{ postcode: "1071DJ", huisnummer: "6" }],
    },
  },
  {
    id: "paleis-op-de-dam",
    tier: "not_scored",
    reason: "government",
    label: { en: "Royal Palace — not scored", nl: "Koninklijk Paleis — niet beoordeeld" },
    rationale: {
      en: "The Royal Palace on Dam Square is a state ceremonial building. It is not a market asset and is not scored.",
      nl: "Het Koninklijk Paleis op de Dam is een ceremonieel staatsgebouw. Dit is geen marktobject en wordt niet beoordeeld.",
    },
    match: {
      addresses: [{ postcode: "1001AM", huisnummer: "1" }],
    },
  },
  {
    id: "binnenhof",
    tier: "not_scored",
    reason: "government",
    label: { en: "Parliament — not scored", nl: "Parlementsgebouw — niet beoordeeld" },
    rationale: {
      en: "The Binnenhof is the seat of the Dutch parliament. It is not a market asset.",
      nl: "Het Binnenhof is de zetel van de Staten-Generaal. Dit is geen marktobject.",
    },
    match: {
      addresses: [{ postcode: "2513AA", huisnummer: "1" }],
    },
  },
  {
    id: "nationaal-monument-dam",
    tier: "not_scored",
    reason: "memorial",
    label: { en: "War memorial — not scored", nl: "Oorlogsmonument — niet beoordeeld" },
    rationale: {
      en: "The National Monument on Dam Square commemorates the victims of World War II. Memorials are not scored.",
      nl: "Het Nationaal Monument op de Dam herdenkt de slachtoffers van de Tweede Wereldoorlog. Monumenten worden niet beoordeeld.",
    },
    match: {
      // Dam Square has no street number for the monument itself; match via Rijksmonument number.
      // Rijksmonument 518350 per RCE (verify).
      monumentNumbers: [518350],
    },
  },
  {
    id: "westerbork-memorial",
    tier: "not_scored",
    reason: "memorial",
    label: { en: "Memorial camp — not scored", nl: "Herinneringskamp — niet beoordeeld" },
    rationale: {
      en: "Kamp Westerbork is a site of remembrance. Memorials are not scored.",
      nl: "Kamp Westerbork is een herinneringsplaats. Monumenten worden niet beoordeeld.",
    },
    match: {
      addresses: [{ postcode: "9431RD", huisnummer: "4" }],
    },
  },
];

// ── Helpers ──

function normPostcode(p: string | undefined | null): string | null {
  if (!p) return null;
  return p.replace(/\s+/g, "").toUpperCase();
}

function normStr(s: string | undefined | null): string | null {
  if (!s) return null;
  // Case- and diacritic-insensitive so "'s-Gravenhage" ↔ "s-Gravenhage" don't miss.
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
}

/**
 * Extract the 16-digit BAG pand id from either the bare form
 * (`0363100012172926`) or the NEN 3610 urn (`NL.IMBAG.Pand.0363100012172926`).
 */
function normBagId(id: string | undefined | null): string | null {
  if (!id) return null;
  const m = id.match(/(\d{16})$/);
  return m ? m[1] : null;
}

function matchesDenylist(plot: PlotData, entry: DenylistEntry): boolean {
  const m = entry.match;

  // Monument number — strongest signal
  if (m.monumentNumbers?.length && plot.heritage?.monumentNumber != null) {
    if (m.monumentNumbers.includes(plot.heritage.monumentNumber)) return true;
  }

  // BAG pand id — normalise both sides so bare and urn forms interoperate
  if (m.bagIds?.length && plot.buildings?.length) {
    const plotBagIds = new Set(
      plot.buildings.map(b => normBagId(b.bagId)).filter((v): v is string => v != null)
    );
    const wantIds = m.bagIds.map(normBagId).filter((v): v is string => v != null);
    if (wantIds.some(id => plotBagIds.has(id))) return true;
  }

  // Monument name substring — the name comes from RCE's authoritative registry
  if (m.monumentNameContains?.length && plot.heritage?.monumentName) {
    const name = normStr(plot.heritage.monumentName);
    if (name) {
      for (const needle of m.monumentNameContains) {
        const n = normStr(needle);
        if (n && name.includes(n)) return true;
      }
    }
  }

  // Address — postcode + huisnummer
  if (m.addresses?.length && plot.addresses?.length) {
    for (const candidate of m.addresses) {
      const wantPc = normPostcode(candidate.postcode);
      const wantNr = candidate.huisnummer;
      for (const addr of plot.addresses) {
        if (normPostcode(addr.postcode) === wantPc && addr.huisnummer === wantNr) return true;
      }
    }
  }

  // Street + city + huisnummer — postcode-free fallback
  if (m.streetAddresses?.length && plot.addresses?.length) {
    for (const candidate of m.streetAddresses) {
      const wantStreet = normStr(candidate.straat);
      const wantCity = normStr(candidate.woonplaats);
      const wantNrs = new Set(candidate.huisnummers);
      for (const addr of plot.addresses) {
        if (
          normStr(addr.straat) === wantStreet &&
          normStr(addr.woonplaats) === wantCity &&
          addr.huisnummer != null &&
          wantNrs.has(addr.huisnummer)
        ) return true;
      }
    }
  }

  return false;
}

// ── Rule-based detection ──

/**
 * Check heritage metadata for memorial / religious categories. Based on the
 * RCE Rijksmonumenten `monumentFunction` and `monumentCategory` fields, which
 * are free-text but follow conventional vocabulary.
 */
function heritageExclusion(plot: PlotData): ScoreExclusion | null {
  const h = plot.heritage;
  if (!h?.loaded || !h.isRijksmonument) return null;

  const cat = (h.monumentCategory ?? "").toLowerCase();
  const fn = (h.monumentFunction ?? "").toLowerCase();

  // Grave monuments, cemeteries → memorial
  if (/grafmonument|begraafplaats|grafteken/.test(cat) || /grafmonument|begraafplaats|oorlogsmonument|gedenk|herdenk|memoriaal/.test(fn)) {
    return {
      tier: "not_scored",
      reason: "memorial",
      label: { en: "Memorial — not scored", nl: "Gedenkplaats — niet beoordeeld" },
      rationale: {
        en: "Memorial and remembrance sites are not scored as market assets.",
        nl: "Gedenk- en herinneringsplaatsen worden niet als marktobject beoordeeld.",
      },
      source: "heritage_api",
    };
  }

  // Active religious buildings (churches, mosques, synagogues, temples)
  // Only treat as worship-exclusion when the PRIMARY function is religious —
  // many deconsecrated churches are now cafes, homes, or offices and should score normally.
  if (/synagoge|moskee/.test(cat) || /synagoge|moskee|tempel/.test(fn)) {
    return {
      tier: "not_scored",
      reason: "worship",
      label: { en: "Place of worship — not scored", nl: "Gebedshuis — niet beoordeeld" },
      rationale: {
        en: "Active places of worship serve a community function and are not scored as commercial assets.",
        nl: "Actieve gebedshuizen hebben een gemeenschapsfunctie en worden niet als commercieel object beoordeeld.",
      },
      source: "heritage_api",
    };
  }

  // Otherwise it's a monument still in private/commercial use (monument residence,
  // restored canal house, museum in a monument building) — fall through to caveat.
  return {
    tier: "caveat",
    reason: "heritage_private",
    label: { en: "National monument", nl: "Rijksmonument" },
    rationale: {
      en: "This property is a listed national monument. Alterations are heavily restricted — factor this in when interpreting the score.",
      nl: "Dit object is een Rijksmonument. Wijzigingen zijn sterk beperkt — houd hier rekening mee bij het beoordelen van de score.",
    },
    source: "heritage_api",
  };
}

/**
 * Check BAG `gebruiksdoel` on addresses for sensitive public functions.
 * Values follow the BAG vocabulary: onderwijsfunctie, gezondheidszorgfunctie,
 * celfunctie, bijeenkomstfunctie, industriefunctie, sportfunctie, etc.
 */
function bagFunctionExclusion(plot: PlotData): ScoreExclusion | null {
  const doelen = (plot.addresses ?? [])
    .map(a => (a.gebruiksdoel ?? "").toLowerCase())
    .filter(Boolean);
  if (doelen.length === 0) return null;

  // Every address must share the flagged function — a mixed-use building with
  // a school on the ground floor and apartments above should still be scored.
  const allMatch = (re: RegExp) => doelen.every(d => re.test(d));

  if (allMatch(/celfunctie/)) {
    return {
      tier: "not_scored",
      reason: "detention",
      label: { en: "Detention facility — not scored", nl: "Detentie-inrichting — niet beoordeeld" },
      rationale: {
        en: "Detention facilities are operated by the Dutch government and are not market assets.",
        nl: "Detentie-inrichtingen zijn in beheer van de overheid en zijn geen marktobject.",
      },
      source: "bag_function",
    };
  }

  if (allMatch(/onderwijsfunctie/)) {
    return {
      tier: "context_only",
      reason: "school",
      label: { en: "School — context only", nl: "School — alleen context" },
      rationale: {
        en: "Educational buildings rarely trade on the open market. Sub-scores are shown for context only.",
        nl: "Onderwijsgebouwen komen zelden op de markt. Subscores zijn uitsluitend ter context.",
      },
      source: "bag_function",
    };
  }

  if (allMatch(/gezondheidszorgfunctie/)) {
    return {
      tier: "context_only",
      reason: "healthcare",
      label: { en: "Healthcare — context only", nl: "Zorg — alleen context" },
      rationale: {
        en: "Healthcare facilities rarely trade on the open market. Sub-scores are shown for context only.",
        nl: "Zorginstellingen komen zelden op de markt. Subscores zijn uitsluitend ter context.",
      },
      source: "bag_function",
    };
  }

  return null;
}

/**
 * Check zoning main category for non-market land uses (parks, water, traffic).
 * Only fires when there is no residential building on the plot — a house in a
 * `wonen`-and-`groen` overlay should still be scored.
 */
function zoningExclusion(plot: PlotData): ScoreExclusion | null {
  const primary = (plot.zoning?.[0]?.bestemmingshoofdgroep ?? "").toLowerCase();
  if (!primary) return null;

  const hasResidential = (plot.addresses ?? []).some(a =>
    (a.gebruiksdoel ?? "").toLowerCase().includes("woonfunctie")
  );
  if (hasResidential) return null;

  if (primary === "groen") {
    return {
      tier: "context_only",
      reason: "park",
      label: { en: "Public green space", nl: "Openbaar groen" },
      rationale: {
        en: "Public parks and green space are not market assets. Sub-scores are shown for context only.",
        nl: "Openbaar groen is geen marktobject. Subscores zijn uitsluitend ter context.",
      },
      source: "zoning",
    };
  }

  if (primary === "water") {
    return {
      tier: "not_scored",
      reason: "water",
      label: { en: "Surface water — not scored", nl: "Oppervlaktewater — niet beoordeeld" },
      rationale: {
        en: "Surface water is not a developable market asset.",
        nl: "Oppervlaktewater is geen ontwikkelbaar marktobject.",
      },
      source: "zoning",
    };
  }

  if (primary === "verkeer") {
    return {
      tier: "context_only",
      reason: "infrastructure",
      label: { en: "Public infrastructure", nl: "Openbare infrastructuur" },
      rationale: {
        en: "Roads, rail, and public-transport land are not market assets. Sub-scores are shown for context only.",
        nl: "Wegen, spoor en OV-terrein zijn geen marktobject. Subscores zijn uitsluitend ter context.",
      },
      source: "zoning",
    };
  }

  return null;
}

// ── Public API ──

/**
 * Return an exclusion descriptor if the plot should not receive a standard
 * PlotScore, or `null` if it should score normally.
 *
 * Priority: denylist > heritage memorial/worship > BAG function > zoning.
 * The denylist wins because it's the editorially-curated safety net — a rule
 * miss should never override a human decision to suppress a specific site.
 */
export function detectExclusion(plot: PlotData): ScoreExclusion | null {
  // 1. Curated denylist — highest priority
  for (const entry of DENYLIST) {
    if (matchesDenylist(plot, entry)) {
      return {
        tier: entry.tier,
        reason: entry.reason,
        label: entry.label,
        rationale: entry.rationale,
        source: "denylist",
        denylistId: entry.id,
      };
    }
  }

  // 2. Heritage-based (memorial, worship) — a caveat fallback is returned for
  //    private monuments, but we keep checking the rule-based sources below
  //    because a stricter tier (e.g. context_only park) should still win.
  const fromHeritage = heritageExclusion(plot);
  if (fromHeritage && fromHeritage.tier === "not_scored") return fromHeritage;

  // 3. BAG function — prison / school / hospital
  const fromBag = bagFunctionExclusion(plot);
  if (fromBag) return fromBag;

  // 4. Zoning — park, water, infrastructure
  const fromZoning = zoningExclusion(plot);
  if (fromZoning) return fromZoning;

  // 5. Fall back to the heritage caveat, if any
  if (fromHeritage) return fromHeritage;

  return null;
}
